
import React from 'react';
import { useState } from 'react';
import { GoogleGenAI } from '@google/genai';
import { getStudioApiKey } from '../utils/audio';

interface VisualStudioProps {
  ui: any;
}

const VisualStudio: React.FC<VisualStudioProps> = ({ ui }) => {
  const t = ui.visual;
  const [prompt, setPrompt] = useState('');
  const [status, setStatus] = useState<'IDLE' | 'GENERATING' | 'ANIMATING'>('IDLE');
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [assetType, setAssetType] = useState<'IMAGE' | 'VIDEO'>('IMAGE');
  const [logs, setLogs] = useState<string[]>([]);

  const addLog = (msg: string) => setLogs(prev => [...prev.slice(-4), `[${new Date().toLocaleTimeString()}] ${msg}`]);

  // Mandatory Key Selection for Gemini 3 Pro and Veo
  const checkKeySelection = async () => {
    const aiStudio = (window as any).aistudio;
    if (aiStudio && typeof aiStudio.hasSelectedApiKey === 'function') {
      const hasKey = await aiStudio.hasSelectedApiKey();
      if (!hasKey) {
        addLog("Awaiting Billing Authorization...");
        await aiStudio.openSelectKey();
      }
    }
  };

  const generateImage = async () => {
    const apiKey = getStudioApiKey();
    if (!prompt.trim() || !apiKey) {
      addLog("Handshake Failed: Vault Key Missing.");
      return;
    }
    
    await checkKeySelection();
    
    setStatus('GENERATING');
    setAssetType('IMAGE');
    addLog(t.log_connecting);

    try {
      const ai = new GoogleGenAI({ apiKey });
      const response = await ai.models.generateContent({
        model: 'gemini-3-pro-image-preview',
        contents: { parts: [{ text: prompt }] },
        config: { imageConfig: { aspectRatio: "16:9", imageSize: "1K" } }
      });

      let imageUrl = '';
      for (const part of response.candidates?.[0]?.content?.parts || []) {
        if (part.inlineData) imageUrl = `data:image/png;base64,${part.inlineData.data}`;
      }

      if (imageUrl) {
        setResultUrl(imageUrl);
        addLog(t.log_ready);
      }
    } catch (e: any) { 
      console.error("Image Generation Failure:", e);
      if (e.message?.includes("Requested entity was not found")) {
        addLog("Protocol Violation: Key Invalidated.");
        const aiStudio = (window as any).aistudio;
        if (aiStudio) await aiStudio.openSelectKey();
      } else {
        addLog(t.log_error);
      }
    } 
    finally { setStatus('IDLE'); }
  };

  const animateWithVeo = async () => {
    const apiKey = getStudioApiKey();
    if (!resultUrl || !apiKey) return;
    
    await checkKeySelection();

    setStatus('ANIMATING');
    addLog(t.log_veo_linking);
    try {
      const ai = new GoogleGenAI({ apiKey });
      const base64Img = resultUrl.split(',')[1];
      let operation = await ai.models.generateVideos({
        model: 'veo-3.1-fast-generate-preview',
        prompt: `Animate: ${prompt}`,
        image: { imageBytes: base64Img, mimeType: 'image/png' },
        config: { numberOfVideos: 1, resolution: '720p', aspectRatio: '16:9' }
      });
      while (!operation.done) {
        await new Promise(r => setTimeout(r, 8000));
        operation = await ai.operations.getVideosOperation({ operation: operation });
      }
      const downloadLink = operation.response?.generatedVideos?.[0]?.video?.uri;
      if (downloadLink) {
        const response = await fetch(`${downloadLink}&key=${apiKey}`);
        const blob = await response.blob();
        setResultUrl(URL.createObjectURL(blob));
        setAssetType('VIDEO');
        addLog(t.log_complete);
      }
    } catch (e: any) { 
      console.error("Veo Animation Failure:", e);
      if (e.message?.includes("Requested entity was not found")) {
        addLog("Re-authorizing BYOK protocol...");
        const aiStudio = (window as any).aistudio;
        if (aiStudio) await aiStudio.openSelectKey();
      } else {
        addLog(t.log_error);
      }
    } 
    finally { setStatus('IDLE'); }
  };

  return (
    <div className="flex-1 flex flex-col space-y-8 animate-in fade-in duration-700 max-w-6xl mx-auto w-full text-left">
      <div className="flex items-center justify-between border-b border-white/10 pb-6">
        <div>
          <h2 className="text-4xl font-black text-white tracking-tighter uppercase">{t.title}</h2>
          <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest mt-1">{t.subtitle}</p>
        </div>
        <div className="flex gap-4">
           {logs.map((l, i) => <div key={i} className="text-[9px] font-mono text-groove-accent">{l}</div>)}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
            <div className="relative aspect-video rounded-[3rem] bg-black/40 border border-white/10 flex items-center justify-center overflow-hidden">
               {resultUrl ? (
                 assetType === 'IMAGE' ? <img src={resultUrl} className="w-full h-full object-cover" /> : <video src={resultUrl} controls autoPlay loop className="w-full h-full object-cover" />
               ) : (
                 <div className="text-center space-y-4 opacity-40">
                    <i className="fa-solid fa-mountain-sun text-6xl text-white"></i>
                    <p className="text-[10px] font-black uppercase tracking-widest text-white">{t.awaiting}</p>
                 </div>
               )}
               {status !== 'IDLE' && (
                 <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center">
                    <div className="w-12 h-12 border-4 border-groove-accent border-t-transparent rounded-full animate-spin"></div>
                 </div>
               )}
            </div>
            {resultUrl && status === 'IDLE' && assetType === 'IMAGE' && (
              <button onClick={animateWithVeo} className="w-full py-5 bg-white text-black rounded-2xl font-black uppercase tracking-widest text-[10px] shadow-2xl hover:scale-[1.01] active:scale-[0.99] transition-all">
                {t.animate_btn}
              </button>
            )}
        </div>

        <div className="space-y-8 bg-black/40 p-10 rounded-[3rem] border border-white/10 backdrop-blur-3xl">
           <div className="space-y-4">
              <label className="text-[10px] font-black uppercase tracking-widest text-groove-accent">{t.prompt_label}</label>
              <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={t.prompt_placeholder} className="w-full bg-white/5 border border-white/20 rounded-2xl px-6 py-5 text-white text-sm outline-none resize-none h-40 focus:border-groove-accent transition-colors" />
           </div>
           <button onClick={generateImage} disabled={status !== 'IDLE' || !prompt.trim()} className="w-full py-6 bg-white text-black rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-groove-accent hover:text-white disabled:opacity-20 transition-all shadow-xl">
             {t.generate_btn}
           </button>
           <p className="text-[8px] text-slate-500 font-bold uppercase tracking-[0.2em] text-center">Powered by Gemini 3 Pro Imagineering Pipeline</p>
        </div>
      </div>
    </div>
  );
};

export default VisualStudio;
